import {
  type InfiniteData,
  type Query,
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import * as Sentry from '@sentry/react';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import {
  type ApiRoutersPortfoliosAssetsImagesCollectionsCollectionsImageViewFields,
  type SearchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGetData,
  type SearchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGetError,
  type SearchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGetResponse,
  searchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGet,
} from '../../generated';
import { extractTotalCount } from '../../utils';
import { PENDING_STALE_TIMEOUT_MS, getPendingImageTimestamps, pollWhilePending } from '../../utils/poll-while-pending';

/**
 * The pair to score each image against; leave all unset for the listing without pair scores. `benchmark_id` (a
 * custom benchmark) and `retailer` (a Vizit benchmark) exclude each other.
 */
export type CollectionImagesPairParams = Pick<
  NonNullable<SearchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGetData['query']>,
  'product_category_id' | 'benchmark_id' | 'retailer'
>;

/** An image of the listing, with its score for the requested pair when one was asked for. */
export type CollectionImageView =
  SearchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGetResponse[number];

/** The pair params as the request sends them: only the ones set. */
function toPairQuery({ product_category_id, benchmark_id, retailer }: CollectionImagesPairParams) {
  return {
    ...(product_category_id ? { product_category_id } : {}),
    ...(benchmark_id ? { benchmark_id } : {}),
    ...(retailer ? { retailer } : {}),
  };
}

export type SearchCollectionImagesQueryVariables =
  SearchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGetData['path'] &
    NonNullable<
      SearchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGetData['query']
    > &
    CollectionImagesPairParams;

export type SearchCollectionImagesQuery = {
  data: CollectionImageView[];
  total_count: number;
  fetchedAt?: number;
};

export const useSearchCollectionImagesQuery = <
  TData = SearchCollectionImagesQuery,
  TError = SearchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGetError,
>(
  {
    workspace_id,
    collection_id,
    sort_by,
    parent_id,
    source,
    exclude_source,
    image_ids,
    classification,
    tag_ids,
    limit,
    offset,
    sort_direction,
    include_artifacts,
    product_category_id,
    benchmark_id,
    retailer,
  }: SearchCollectionImagesQueryVariables,
  options?: Omit<UseQueryOptions<SearchCollectionImagesQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<SearchCollectionImagesQuery, TError, TData>({
    queryKey: [
      'SearchCollectionImages',
      {
        workspace_id,
        collection_id,
        sort_by,
        parent_id,
        source,
        exclude_source,
        image_ids,
        classification,
        tag_ids,
        limit,
        offset,
        sort_direction,
        include_artifacts,
        product_category_id,
        benchmark_id,
        retailer,
      },
    ],
    queryFn: async () => {
      const { data, response } =
        await searchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGet({
          path: { workspace_id, collection_id },
          query: {
            sort_by: sort_by as ApiRoutersPortfoliosAssetsImagesCollectionsCollectionsImageViewFields | undefined,
            parent_id,
            source: source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
            exclude_source: exclude_source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
            image_ids: image_ids as string[] | undefined,
            classification,
            limit,
            offset,
            sort_direction,
            include_artifacts,
            ...toPairQuery({ product_category_id, benchmark_id, retailer }),
            ...(tag_ids?.length ? { tag_ids } : {}),
          },
          throwOnError: true,
        });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    ...options,
  });
};

useSearchCollectionImagesQuery.getKey = (variables: SearchCollectionImagesQueryVariables) => [
  'SearchCollectionImages',
  variables,
];

useSearchCollectionImagesQuery.fetcher = (
  {
    workspace_id,
    collection_id,
    sort_by,
    parent_id,
    source,
    exclude_source,
    image_ids,
    classification,
    tag_ids,
    limit,
    offset,
    sort_direction,
    include_artifacts,
    product_category_id,
    benchmark_id,
    retailer,
  }: SearchCollectionImagesQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } =
      await searchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGet({
        path: { workspace_id, collection_id },
        query: {
          sort_by: sort_by as ApiRoutersPortfoliosAssetsImagesCollectionsCollectionsImageViewFields | undefined,
          parent_id,
          source: source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
          exclude_source: exclude_source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
          image_ids: image_ids as string[] | undefined,
          classification,
          limit,
          offset,
          sort_direction,
          include_artifacts,
          ...toPairQuery({ product_category_id, benchmark_id, retailer }),
          ...(tag_ids?.length ? { tag_ids } : {}),
        },
        throwOnError: true,
      });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};

export type SearchCollectionImagesInfiniteQueryVariables = Omit<SearchCollectionImagesQueryVariables, 'offset'>;

export const useSearchCollectionImagesInfiniteQuery = <
  TData = SearchCollectionImagesQuery,
  TError = SearchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGetError,
>(
  {
    workspace_id,
    collection_id,
    sort_by,
    parent_id,
    source,
    exclude_source,
    image_ids,
    classification,
    tag_ids,
    limit,
    sort_direction,
    include_artifacts,
    product_category_id,
    benchmark_id,
    retailer,
  }: SearchCollectionImagesInfiniteQueryVariables,
  {
    awaitsScores = true,
    ...options
  }: Omit<
    UseInfiniteQueryOptions<SearchCollectionImagesQuery, TError, TData>,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  > & {
    /** Whether a scoring can fill in the images' missing scores; without one only images awaiting their file are re-read */
    awaitsScores?: boolean;
  } = {},
) => {
  return useInfiniteQuery<SearchCollectionImagesQuery, TError, TData>({
    queryKey: useSearchCollectionImagesInfiniteQuery.getKey({
      workspace_id,
      collection_id,
      sort_by,
      parent_id,
      source,
      exclude_source,
      image_ids,
      classification,
      tag_ids,
      limit,
      sort_direction,
      include_artifacts,
      product_category_id,
      benchmark_id,
      retailer,
    }),
    queryFn: async ({ pageParam = 0 }) => {
      const { data, response } =
        await searchCollectionImagesV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdImagesGet({
          path: { workspace_id, collection_id },
          query: {
            sort_by: sort_by as ApiRoutersPortfoliosAssetsImagesCollectionsCollectionsImageViewFields | undefined,
            parent_id,
            source: source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
            exclude_source: exclude_source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
            image_ids: image_ids as string[] | undefined,
            classification,
            limit,
            offset: pageParam as number,
            sort_direction,
            include_artifacts,
            ...toPairQuery({ product_category_id, benchmark_id, retailer }),
            ...(tag_ids?.length ? { tag_ids } : {}),
          },
          throwOnError: true,
        });

      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
        fetchedAt: Date.now(),
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const pageSize = limit || 12; // Default to 12 if no limit specified
      const currentOffset = (allPages.length - 1) * pageSize;
      const nextOffset = currentOffset + pageSize;

      // Check if we have more data to fetch
      if (lastPage.data.length < pageSize || nextOffset >= lastPage.total_count) {
        return undefined; // No more pages
      }

      return nextOffset;
    },
    getPreviousPageParam: (_, allPages) => {
      // Return undefined when we're on the first/only page (initial load)
      // Return a value when we have multiple pages (subsequent loads)
      return allPages.length <= 1 ? undefined : 0;
    },
    initialPageParam: 0,
    refetchInterval: pollWhilePending(
      (query: Query<SearchCollectionImagesQuery, TError, InfiniteData<SearchCollectionImagesQuery, unknown>>) =>
        getPendingImageTimestamps(
          (query.state.data?.pages ?? []).flatMap((page) => page.data),
          { awaitsScores },
        ),
      {
        onTimeout: (pendingCount) => {
          Sentry.withScope((scope) => {
            scope.setFingerprint([
              'GET',
              '/v1/workspaces/{workspace_id}/assets/images/collections/{collection_id}/images',
              'polling_timeout',
            ]);

            Sentry.captureException(
              new Error('Images still pending after stale window for search collection images query.'),
              {
                level: 'warning',
                tags: {
                  feature: SENTRY_FEATURE_TAGS.Collection,
                  stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Collection].collectionAssetPolling,
                },
                extra: {
                  workspace_id,
                  collection_id,
                  pending_count: pendingCount,
                  stale_window_ms: PENDING_STALE_TIMEOUT_MS,
                  query_variables: {
                    workspace_id,
                    collection_id,
                    sort_by,
                    parent_id,
                    source,
                    exclude_source,
                    image_ids,
                    classification,
                    tag_ids,
                    limit,
                    sort_direction,
                    include_artifacts,
                    product_category_id,
                    benchmark_id,
                    retailer,
                  },
                },
              },
            );
          });
        },
      },
    ),
    refetchIntervalInBackground: true,
    ...options,
  });
};

useSearchCollectionImagesInfiniteQuery.getKey = ({
  workspace_id,
  collection_id,
  sort_by,
  parent_id,
  source,
  exclude_source,
  image_ids,
  classification,
  tag_ids,
  limit,
  sort_direction,
  include_artifacts,
  product_category_id,
  benchmark_id,
  retailer,
}: SearchCollectionImagesInfiniteQueryVariables) => [
  'SearchCollectionImagesInfinite',
  {
    workspace_id,
    collection_id,
    sort_by,
    parent_id,
    source,
    exclude_source,
    image_ids,
    classification,
    tag_ids,
    limit,
    sort_direction,
    include_artifacts,
    product_category_id,
    benchmark_id,
    retailer,
  },
];
